import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map, retry } from 'rxjs/operators';
import { throwError, timer } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ReviewService {
  // 6019 = el backend no pudo conectarse con la OTA. Suele ser transitorio, así que
  // reintentamos; cualquier otro código corta el reintento de inmediato.
  private readonly OTA_CONNECTION_ERROR_CODE = 6019;
  private readonly REPLY_RETRY_COUNT = 2; // 3 intentos en total
  private readonly REPLY_RETRY_DELAY_MS = 500;

  constructor(private http: HttpClient) {}

  public findReviewsV2(
    dates: any,
    customerId: string,
    languageId: string,
    page: number,
    page_size: number,
    paged = true,
    body = null,
  ) {
    const url = `${environment.onlineListReviewsUrl}/v2`;
    let params: HttpParams = new HttpParams()
      .set('start_date', dates.startDate)
      .set('end_date', dates.endDate)
      .set('customer_id', customerId)
      .set('language_id', languageId)
      .set('paged', paged)
      .set('page', page)
      .set('page_size', page_size);

    if (body) {
      params = params
        .set('otas', body?.otas || '')
        .set('answered_flag', body?.answered_flag === 0 ? 0 : body?.answered_flag || '')
        .set('nps', body?.nps || '')
        .set('classification', body?.calification || '')
        .set('traveler_type', body?.traveler_type || '')
        .set('language_list', body?.language_list || '')
        .set('cases', body?.cases || '');
    }

    return this.http.get(url, { params: params }).pipe(map((response) => response));
  }

  public findReviewById(reviewId: string, languageId: string) {
    const url = `${environment.onlineReviews}/${reviewId}`;
    const params: HttpParams = new HttpParams().set('language_id', languageId);

    return this.http.get(url, { params: params }).pipe(map((response) => response));
  }

  public findReviews(dates: any, customerId: string, reviewId = '/', cache: boolean = true, languageId) {
    let url = `${environment.onlineListReviewsUrl}`;
    if (reviewId !== '/') url = url + '/' + reviewId;
    const params: HttpParams = new HttpParams()
      .set('start_date', dates.startDate)
      .set('end_date', dates.endDate)
      .set('customer_id', customerId)
      .set('language_id', languageId);

    return this.http.get(url, { params: params }).pipe(map((response) => response));
  }

  public translateReview(reviewId, sourceLanguage, targetLanguage, productId, title, description) {
    const url = `${environment.customerPath}translate`;
    const body = {
      source_language_id: sourceLanguage,
      target_language_id: targetLanguage,
      product_id: productId,
      comment_id: reviewId,
      title: title,
      description: description,
    };

    return this.http.post(url, body).pipe(map((response) => response));
  }

  public setReview(reviewId) {
    const url = `${environment.onlineReviews}/${reviewId}/status`;
    const params = new HttpParams().set('status', 1);

    return this.http.patch(url, {}, { params });
  }

  public deleteReview(reviewId) {
    let url = `${environment.onlineReviews}`;
    if (reviewId !== '/') url = url + '/' + reviewId;

    return this.http.delete(url).pipe(map((response) => response));
  }

  public getReviewsDummy() {
    const params: HttpParams = new HttpParams();
    return this.http.get(`${environment.onlineListReviewsJson}`, { params: params }).pipe(map((response) => response));
  }

  replyReview(reviewId, comment, customer_id) {
    const url = `${environment.onlineReviews}/${reviewId}/reply`;
    const headers = new HttpHeaders().set('skip', 'skip');
    const body = {
      comment,
      customer_id,
    };
    return this.http.post(url, body, { headers }).pipe(
      retry({
        count: this.REPLY_RETRY_COUNT,
        delay: (error) =>
          error?.code === this.OTA_CONNECTION_ERROR_CODE
            ? timer(this.REPLY_RETRY_DELAY_MS)
            : throwError(() => error),
      }),
    );
  }
}
